import React, { useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from "react-redux";
import {
  getCarParkBookings,
  selectCarParkingPlanBookings,
  selectSelectedParkingPlanId
} from "../../ducks/carParkingMapView.duck";
import styled from "styled-components";
import Colours from '@shared/ui/atoms/Colours';
import { TransformComponent, TransformWrapper } from "react-zoom-pan-pinch";
import { CarParkSpace } from '@features/whereabouts/services/CarParkingPlanService';
import { ParkingSpaceOverlay } from "./ParkingSpaceOverlay";
import { selectActiveDay } from "../../../../store/editMovements.duck";
import { DATE_FORMAT } from "@shared/utils/DateUtils";
import { BodyVerySmall } from '@shared/ui/atoms/fonts/Body';
import { MapButtonRow, MapControlButton, MapControlsContainer } from "../ViewComponents";
import {
  selectHoveringCarParkSpace,
  selectSelectedCarParkingPlan,
  selectSelectedCarParkSpace,
  setSelectedCarParkSpace
} from "../../../../store/officeView.duck";
import { SelectedCarParkingSpaceOverlay } from "./SelectedCarParkingSpaceOverlay";
import { OutlineButton } from '@shared/ui/atoms/buttons/OutlineButton';
import { DialogIdentifiers, openDialogWithPayload } from "@shared/store/dialog.duck";
import { IsSuperAdmin } from "@shared/hooks/IsSuperAdmin";
import { tablet } from '@shared/ui/atoms/MediaQueries';
import { isInTeams } from "@shared/utils/TeamsUtils";
import { HoveringCarParkingSpaceOverlay } from "./HoveringCarParkingSpaceOverlay";
import { ButtonSize } from '@shared/ui/atoms/buttons/ButtonStyling';

export function CarParkingMapView(props: Props) {
  const dispatch = useDispatch();
  const myRef = useRef(null);
  const selectedParkingPlan = useSelector(selectSelectedCarParkingPlan);
  const selectedCarParkPlanId = useSelector(selectSelectedParkingPlanId);
  const carParkBookings = useSelector(selectCarParkingPlanBookings);
  const selectedDate = useSelector(selectActiveDay);
  const selectedCarParkSpace = useSelector(selectSelectedCarParkSpace);
  const hoveringCarParkSpace = useSelector(selectHoveringCarParkSpace);
  const dateString = selectedDate?.format(DATE_FORMAT);

  const carParkMap = selectedParkingPlan?.carParkMap
  const carParkMapUrl = selectedParkingPlan?.carParkPlan;
  const carParkSpaces = selectedParkingPlan?.parkingSpaces;

  const carParkPlanId = selectedCarParkPlanId || selectedParkingPlan?.id;

  useEffect(() => {
    if (dateString && carParkPlanId) {
      dispatch(getCarParkBookings({carParkPlanId: carParkPlanId, dateString: dateString}));
    }
  }, [dispatch, dateString, carParkPlanId]);

  const encodedCarParkMap = useMemo(() => {
    return carParkMap ? encodeURIComponent(carParkMap) : undefined;
  }, [carParkMap])

  const clearSelection = () => {
    dispatch(setSelectedCarParkSpace(undefined));
  }

  const onCarParkSpaceClicked = (carParkSpace: CarParkSpace) => {
    if (props.onCarParkingSpaceSelected) {
      props.onCarParkingSpaceSelected(carParkSpace);
    }
  }

  const onBackgroundClick = (e: MouseEvent) => {
    e.stopPropagation();
  }

  const superAdminButtonsEnabled = IsSuperAdmin();

  const onAddNewSpaceClicked = () => {
    dispatch(openDialogWithPayload({
      payload: { parkingSpace: {carParkPlanId: carParkPlanId, label: 'New space'} },
      activeDialog: DialogIdentifiers.EditParkingSpaceDialog,
    }));
  }

  return (
    <Container onClick={(e: any) => onBackgroundClick(e)}>
      <MapWrapper isInTeams={isInTeams()}>

        {(encodedCarParkMap || carParkMapUrl) && <>
          <TransformWrapper centerOnInit={true} initialScale={0.5} maxScale={2} minScale={0.5} ref={myRef}>
            {(params: any) => (
              <div style={{position: 'relative', height: '100%', width: '100%'}} onClick={clearSelection}>
                <MapButtonRow>
                  {superAdminButtonsEnabled && <>
                    <OutlineButton text={"button.add-new-space"} size={ButtonSize.Small} click={onAddNewSpaceClicked} />
                  </>}
                  <MapControlsContainer className="tools">
                    <MapControlButton onClick={() => params.zoomIn()}>+</MapControlButton>
                    <MapControlButton onClick={() => params.zoomOut()}>-</MapControlButton>
                    <MapControlButton onClick={() => params.resetTransform()}><BodyVerySmall style={{fontSize: 8}} weight={600}>100%</BodyVerySmall></MapControlButton>
                  </MapControlsContainer>
                </MapButtonRow>

                <TransformComponent wrapperStyle={{ maxWidth: "100%", maxHeight: "100%" }} wrapperClass={'transformComponent'}>

                  {encodedCarParkMap ? <img src={`data:image/svg+xml,${encodedCarParkMap}`} alt="icon"/> :
                    <img src={`/assets/office-layouts/${carParkMapUrl}`} alt={""}/>}

                  {carParkSpaces && carParkSpaces.map((parkingSpace, key) => (
                    <ParkingSpaceOverlay parkingSpace={parkingSpace}
                                         booking={(carParkBookings || []).filter(cpb => cpb.spaceId === parkingSpace.id)}
                                         onClicked={onCarParkSpaceClicked}
                                         isSelected={selectedCarParkSpace?.id === parkingSpace.id}
                                         key={key} />
                  ))}

                  {selectedCarParkSpace && <SelectedCarParkingSpaceOverlay carParkingSpace={selectedCarParkSpace} carParkBookings={carParkBookings} />}
                  {hoveringCarParkSpace && <HoveringCarParkingSpaceOverlay carParkingSpace={hoveringCarParkSpace} carParkBookings={carParkBookings} />}
                </TransformComponent>
              </div>
            )}
          </TransformWrapper>
        </>}
      </MapWrapper>
    </Container>
  )
}

interface Props {
  onCarParkingSpaceSelected?: (carParkSpace: CarParkSpace) => any;
}

const Container = styled.div`
  display: flex;
  max-height: 100%;
`

const MapWrapper = styled.div<any>`
  width: 100%;
  overflow: hidden;
  background-color: ${Colours.veryLightGrey};
  max-height: 100%;
  .transformComponent {
    width: 100vw;
    height: calc(100vh - ${props => props.isInTeams ? '260px' : '310px'});
    @media (${tablet}) {
      height: calc(100vh - ${props => props.isInTeams ? '125px' : '180px'});
    }
  }
`
